import React, { useState, useEffect } from 'react';
import { Calendar, Search, Filter, DollarSign, FileText, Clock, CheckCircle, Building, User, MapPin } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';

const AccountsPaymentDashboard: React.FC = () => {
  const [paymentItems, setPaymentItems] = useState<any[]>([]);
  const [filteredItems, setFilteredItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [statusCounts, setStatusCounts] = useState<any>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    vendor: '',
    dueDateFrom: '',
    dueDateTo: '',
    itinerarySearch: '',
    guestName: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeStatus, setActiveStatus] = useState('PENDING_PAYMENT');
  const [selectedVendorGroup, setSelectedVendorGroup] = useState<any>(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [vendorItems, setVendorItems] = useState<{[key: string]: any[]}>({});
  const [vendorFiles, setVendorFiles] = useState<{[key: string]: any[]}>({});
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [currentFile, setCurrentFile] = useState<any>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState<string | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    status: 'PAYMENT_COMPLETED',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    actual_amount: 0,
    receipt_file: null as File | null
  });

  useEffect(() => {
    loadDashboardData();
  }, [activeStatus]);

  useEffect(() => {
    // Only apply client-side filters if we're not using database search filters
    const hasDbFilters = searchFilters.vendor || searchFilters.dueDateFrom || searchFilters.dueDateTo || searchFilters.itinerarySearch || searchFilters.guestName;
    if (!hasDbFilters) {
      applyClientSideFilters();
    }
  }, [paymentItems, searchQuery, searchFilters]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const applyClientSideFilters = () => {
    let filtered = [...paymentItems];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.itinerary_id?.toString().includes(query) ||
        item.guest_name?.toLowerCase().includes(query) ||
        item.itinerary_name?.toLowerCase().includes(query) ||
        item.vendor_name?.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const handleDatabaseSearch = async () => {
    try {
      setSearching(true);
      const searchParams = {
        status: '', // Don't filter by status when doing database search
        vendor: searchFilters.vendor,
        due_date_from: searchFilters.dueDateFrom,
        due_date_to: searchFilters.dueDateTo,
        itinerary_search: searchFilters.itinerarySearch,
        guest_name: searchFilters.guestName,
        limit: 1000
      };

      const searchRes = await api.post('/accounts-dashboard/payments/search', searchParams);
      
      if (searchRes.data.success) {
        const items = searchRes.data.data;
        console.log('Setting payment items:', items.length, 'items');
        setPaymentItems(items);
        setFilteredItems(items); // Set filtered items directly to search results
        setExpandedVendors(new Set()); // Clear expanded state
        setVendorItems({}); // Clear cached vendor items
        setActiveStatus('ALL'); // Update active status to show all results
      }
    } catch (error) {
      console.error('Error searching payments:', error);
    } finally {
      setSearching(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, searchRes] = await Promise.all([
        api.get('/accounts-dashboard/dashboard-stats'),
        api.post('/accounts-dashboard/payments/search', { 
          status: activeStatus === 'ALL' ? '' : activeStatus, 
          limit: 1000 
        })
      ]);

      if (statsRes.data.success) {
        setStatusCounts(statsRes.data.data.status_totals);
      }

      if (searchRes.data.success) {
        const items = searchRes.data.data;
        setPaymentItems(items);
        setFilteredItems(items);
        setVendorItems({}); // Clear cached vendor items when loading new data
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setStatusCounts({
        NOT_BOOKED: 0,
        BOOKED: 0,
        PENDING_PAYMENT: 0,
        PAYMENT_COMPLETED: 0,
        CANCELED_NO_FEE: 0,
        CANCELED_WITH_FEE: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const groupedByVendor = filteredItems.reduce((acc: any, item: any) => {
    // Consolidate by vendor_id and itinerary_id only for payment purposes
    const vendorKey = `${item.vendor_id}_${item.itinerary_id}`;
    if (!acc[vendorKey]) {
      acc[vendorKey] = {
        vendor_name: item.original_vendor_name || item.vendor_name || 'No Vendor',
        vendor_id: item.vendor_id,
        itinerary_id: item.itinerary_id,
        guest_name: item.guest_name,
        itinerary_name: item.itinerary_name,
        travel_start_date: item.travel_start_date,
        travel_end_date: item.travel_end_date,
        no_of_pax: item.no_of_pax,
        destination: item.destination,
        driver_name: item.driver_name,
        remarks: item.remarks,
        subGroups: [], // Store individual item type groups
        items: [],
        total_amount: 0
      };
    }
    
    // Add to subGroups for detailed breakdown
    const subGroupKey = `${item.vendor_name}_${item.due_date}_${item.status}`;
    let subGroup = acc[vendorKey].subGroups.find((sg: any) => sg.key === subGroupKey);
    if (!subGroup) {
      subGroup = {
        key: subGroupKey,
        vendor_name: item.vendor_name,
        due_date: item.due_date,
        status: item.status,
        payment_category: item.payment_category,
        items: [],
        amount: 0
      };
      acc[vendorKey].subGroups.push(subGroup);
    }
    
    subGroup.items.push(item);
    subGroup.amount += item.actual_amount;
    acc[vendorKey].items.push(item);
    acc[vendorKey].total_amount += item.actual_amount;
    return acc;
  }, {});

  const vendorGroups = Object.values(groupedByVendor);

  const handleVendorExpand = async (vendorKey: string, vendor: any) => {
    const newExpanded = new Set(expandedVendors);
    
    if (expandedVendors.has(vendorKey)) {
      newExpanded.delete(vendorKey);
    } else {
      newExpanded.add(vendorKey);
      
      if (!vendorItems[vendorKey]) {
        try {
          const vendorIdParam = vendor.vendor_id === null || vendor.vendor_id === undefined ? 'null' : vendor.vendor_id;
          const itemsUrl = `/accounts-dashboard/payments/vendor/${vendorIdParam}/itinerary/${vendor.itinerary_id}/items`;
          
          const [itemsRes, filesRes] = await Promise.all([
            api.get(itemsUrl),
            api.get(`/accounts-dashboard/payments/vendor/${vendorIdParam}/itinerary/${vendor.itinerary_id}/files`)
          ]);
          
          if (itemsRes.data.success) {
            setVendorItems(prev => ({...prev, [vendorKey]: itemsRes.data.data}));
          }
          
          if (filesRes.data.success) {
            setVendorFiles(prev => ({...prev, [vendorKey]: filesRes.data.data}));
          }
        } catch (error) {
          console.error('Error loading vendor data:', error);
        }
      }
    }
    
    setExpandedVendors(newExpanded);
  };

  const handleVendorClick = async (vendor: any) => {
    setSelectedVendorGroup(vendor);
    
    // Load payment history first to calculate outstanding amount
    setLoadingHistory(true);
    try {
      const response = await api.get(`/accounts-dashboard/payments/vendor/${vendor.vendor_id}/itinerary/${vendor.itinerary_id}/payment-history`);
      if (response.data.success) {
        const payments = response.data.data.payments || [];
        setPaymentHistory(payments);
        
        // Calculate outstanding amount
        const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.payment_amount || 0), 0);
        const outstanding = vendor.total_amount - totalPaid;
        
        setPaymentForm({
          status: 'PAYMENT_COMPLETED',
          payment_date: new Date().toISOString().split('T')[0],
          payment_reference: '',
          actual_amount: outstanding > 0 ? outstanding : vendor.total_amount,
          receipt_file: null
        });
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
      setPaymentHistory([]);
      setPaymentForm({
        status: 'PAYMENT_COMPLETED',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: '',
        actual_amount: vendor.total_amount,
        receipt_file: null
      });
    } finally {
      setLoadingHistory(false);
    }
    
    setShowVendorModal(true);
  };

  const handleFilePreview = (file: any) => {
    setCurrentFile(file);
    setShowFileViewer(true);
  };

  const handleFileDownload = (file: any) => {
    window.open(file.file_url, '_blank');
  };

  const handleFileDelete = async (file: any, vendorKey: string) => {
    try {
      await api.delete(`/accounts-dashboard/payments/files/${file.id}`);
      setMessage({ type: 'success', text: 'File deleted successfully' });
      // Refresh files for this vendor
      const vendor = vendorGroups.find((v: any) => `${v.vendor_id}_${v.itinerary_id}` === vendorKey) as any;
      if (vendor) {
        const filesRes = await api.get(`/accounts-dashboard/payments/vendor/${vendor.vendor_id}/itinerary/${vendor.itinerary_id}/files`);
        if (filesRes.data.success) {
          setVendorFiles(prev => ({ ...prev, [vendorKey]: filesRes.data.data }));
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete file' });
    }
  };

  const handleReceiptUpload = async (vendor: any, file: File) => {
    const vendorKey = `${vendor.vendor_id}_${vendor.itinerary_id}`;
    try {
      setUploadingReceipt(vendorKey);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vendor_id', vendor.vendor_id.toString());
      formData.append('itinerary_id', vendor.itinerary_id.toString());
      
      await api.post('/accounts-dashboard/payments/upload-receipt', formData);
      setMessage({ type: 'success', text: 'Receipt uploaded successfully' });
      
      // Refresh files
      const filesRes = await api.get(`/accounts-dashboard/payments/vendor/${vendor.vendor_id}/itinerary/${vendor.itinerary_id}/files`);
      if (filesRes.data.success) {
        setVendorFiles(prev => ({ ...prev, [vendorKey]: filesRes.data.data }));
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload receipt' });
    } finally {
      setUploadingReceipt(null);
    }
  };

  const handlePaymentSubmit = async () => {
    // Validate required fields
    if (!paymentForm.actual_amount || paymentForm.actual_amount <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid payment amount' });
      return;
    }
    
    if (!paymentForm.receipt_file) {
      setMessage({ type: 'error', text: 'Please upload a receipt file' });
      return;
    }
    
    // Validate file type and size
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(paymentForm.receipt_file.type)) {
      setMessage({ type: 'error', text: 'Please upload a valid file (PDF, JPG, JPEG, PNG)' });
      return;
    }
    
    if (paymentForm.receipt_file.size > 10 * 1024 * 1024) { // 10MB limit
      setMessage({ type: 'error', text: 'File size must be less than 10MB' });
      return;
    }
    
    try {
      // Create FormData for payment with file
      const formData = new FormData();
      formData.append('payment_amount', paymentForm.actual_amount.toString());
      formData.append('payment_date', paymentForm.payment_date);
      formData.append('payment_reference', paymentForm.payment_reference);
      formData.append('notes', 'Payment via Accounts Dashboard');
      formData.append('receipt_file', paymentForm.receipt_file);
      
      const response = await api.post(`/accounts-dashboard/payments/vendor/${selectedVendorGroup.vendor_id}/itinerary/${selectedVendorGroup.itinerary_id}/add-payment`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setShowVendorModal(false);
        setMessage({ 
          type: 'success', 
          text: `Payment of ${formatCurrency(paymentForm.actual_amount)} added successfully. Outstanding: ${formatCurrency(response.data.outstanding_balance)}` 
        });
        // Reload payment history
        const historyResponse = await api.get(`/accounts-dashboard/payments/vendor/${selectedVendorGroup.vendor_id}/itinerary/${selectedVendorGroup.itinerary_id}/payment-history`);
        if (historyResponse.data.success) {
          setPaymentHistory(historyResponse.data.data.payments || []);
        }
        await loadDashboardData();
      }
    } catch (error) {
      console.error('Error adding payment:', error);
      setMessage({ type: 'error', text: 'Failed to add payment. Please try again.' });
    }
  };

  const StatusCard = ({ title, fullTitle, count, icon, color, isActive, onClick }: any) => (
    <button 
      onClick={onClick}
      className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-200 hover:shadow-lg transform active:scale-95 sm:hover:-translate-y-1 ${
        isActive ? 'ring-2 ring-blue-500 ring-offset-1 sm:ring-offset-2' : ''
      } ${color} text-white touch-manipulation`}
      title={fullTitle || title}
    >
      <div className="flex items-center justify-between mb-1 sm:mb-2">
        <div className="text-white/90 text-xs sm:text-sm font-medium truncate pr-1">
          <span className="sm:hidden">{title}</span>
          <span className="hidden sm:inline">{fullTitle || title}</span>
        </div>
        {icon}
      </div>
      <div className="text-lg sm:text-2xl font-bold text-white">{count}</div>
    </button>
  );

  const VendorPaymentCard = ({ vendor, vendorKey, isExpanded, onExpand, onManagePayment, formatDate }: any) => {
    const items = vendorItems[vendorKey] || [];
    const files = vendorFiles[vendorKey] || [];
    const overallStatus = vendor.items[0]?.status || 'UNKNOWN';
    
    return (
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 sm:p-6">
          {/* Mobile-first layout */}
          <div className="space-y-3 sm:space-y-4">
            {/* Header - Mobile Stack, Desktop Flex */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center space-x-3 cursor-pointer touch-manipulation" onClick={() => {
                console.log('CLICK DETECTED - vendorKey:', vendorKey, 'vendor:', vendor.vendor_name);
                onExpand(vendorKey, vendor);
              }}>
                <span className="text-gray-400 text-lg">{isExpanded ? '▼' : '▶'}</span>
                <Building className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                    {vendor.vendor_name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500">WH_{vendor.itinerary_id}</p>
                </div>
              </div>
              
              {/* Amount and Actions - Mobile Stack */}
              <div className="flex flex-col sm:text-right space-y-2">
                <div className="text-lg sm:text-xl font-bold text-gray-900">{formatCurrency(vendor.total_amount)}</div>
                <div className="flex flex-col gap-2 items-end">
                  <div className="flex gap-2">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium text-center ${
                      overallStatus === 'PENDING_PAYMENT' 
                        ? 'bg-red-100 text-red-800'
                        : overallStatus === 'PARTIALLY_PAID'
                        ? 'bg-yellow-100 text-yellow-800'
                        : overallStatus === 'PAYMENT_COMPLETED'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {overallStatus.replace(/_/g, ' ')}
                    </span>
                    <button 
                      onClick={() => onManagePayment(vendor)}
                      className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-xs sm:text-sm hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation"
                    >
                      Manage Payment
                    </button>
                  </div>
                  {vendor.due_date && (
                    <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-600">
                      <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span>Due: {formatDate(vendor.due_date)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          
            {/* Guest Info - Mobile Optimized */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
              <div className="flex items-center space-x-1 min-w-0">
                <User className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span className="truncate">{vendor.guest_name || 'Guest name not available'}</span>
              </div>
              {vendor.itinerary_name && (
                <div className="flex items-center space-x-1 min-w-0">
                  <span className="truncate">{vendor.itinerary_name}</span>
                </div>
              )}
              {vendor.driver_name && (
                <div className="flex items-center space-x-1 min-w-0">
                  <span className="text-blue-600 font-medium truncate">Driver: {vendor.driver_name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-gray-100 p-4 sm:p-6 bg-gray-50 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-4 sm:space-y-6">
              {/* Item Type Summary */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Payment Summary by Category</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {(() => {
                    const itemTypeSummary = items.reduce((acc: any, item: any) => {
                      if (!acc[item.item_type]) {
                        acc[item.item_type] = { count: 0, amount: 0 };
                      }
                      acc[item.item_type].count += 1;
                      acc[item.item_type].amount += item.actual_amount;
                      return acc;
                    }, {});
                    
                    return Object.entries(itemTypeSummary).map(([itemType, summary]: [string, any]) => (
                      <div key={itemType} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-blue-900 capitalize">{itemType.replace('_', ' ')}</div>
                            <div className="text-xs text-blue-700">{summary.count} items</div>
                          </div>
                          <div className="text-lg font-bold text-blue-900">{formatCurrency(summary.amount)}</div>
                        </div>
                      </div>
                    ));
                  })()
                  }
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Payment Items</h4>
                <div className="space-y-4">
                  {(() => {
                    // Group items by item_type
                    const itemsByType = items.reduce((acc: any, item: any) => {
                      if (!acc[item.item_type]) {
                        acc[item.item_type] = [];
                      }
                      acc[item.item_type].push(item);
                      return acc;
                    }, {});
                    
                    return Object.entries(itemsByType).map(([itemType, typeItems]: [string, any]) => (
                      <div key={itemType} className="space-y-2">
                        <h5 className="font-medium text-gray-800 text-sm capitalize border-b border-gray-200 pb-1">
                          {itemType.replace('_', ' ')} Items
                        </h5>
                        {typeItems.map((item: any) => (
                          <div key={item.id} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-white rounded-lg ml-4">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium text-gray-900 text-sm sm:text-base block truncate">{item.name}</span>
                              <div className="text-xs text-gray-500">{item.item_type} • {item.item_subtype}</div>
                              {item.remarks && (
                                <div className="text-xs text-orange-700 bg-orange-50 p-2 rounded border border-orange-200 mt-1">
                                  <span className="font-medium">Note:</span> {item.remarks}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-row sm:flex-col sm:text-right items-center sm:items-end justify-between sm:justify-start gap-2">
                              <div className="font-medium text-gray-900 text-sm sm:text-base">{formatCurrency(item.actual_amount)}</div>
                              <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                                item.status === 'PENDING_PAYMENT' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                              }`}>
                                {item.status?.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ));
                  })()
                  }
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-3 text-sm sm:text-base">Uploaded Files ({files.length})</h4>
                {files.length > 0 ? (
                  <div className="space-y-3">
                    {files.map((file: any, index: number) => (
                      <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white rounded-lg border">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm truncate">{file.filename}</div>
                            <div className="text-xs text-gray-500">{file.file_type}</div>
                          </div>
                        </div>
                        <div className="flex space-x-2 flex-shrink-0">
                          <button
                            onClick={() => handleFilePreview(file)}
                            className="p-2 text-gray-600 hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors touch-manipulation"
                            title="Preview"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => handleFileDownload(file)}
                            className="p-2 text-green-600 hover:bg-green-50 active:bg-green-100 rounded-lg transition-colors touch-manipulation"
                            title="Download"
                          >
                            ⬇️
                          </button>
                          <button
                            onClick={() => handleFileDelete(file, vendorKey)}
                            className="p-2 text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors touch-manipulation"
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 sm:py-8 text-gray-500">
                    <FileText className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm sm:text-base">No files uploaded yet</p>
                  </div>
                )}
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload Receipt</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => e.target.files?.[0] && handleReceiptUpload(vendor, e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 file:px-3 sm:file:px-4 file:rounded-lg file:border-0 file:text-xs sm:file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:touch-manipulation"
                    disabled={uploadingReceipt === vendorKey}
                  />
                  {uploadingReceipt === vendorKey && (
                    <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Uploading...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading payment dashboard...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Messages */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {message.text}
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Accounts Dashboard</h1>
                <p className="text-gray-600 mt-2">Manage vendor payments and track due dates</p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                <span>{new Date().toLocaleDateString('en-GB')}</span>
              </div>
            </div>
          </div>

          {/* Status Cards - Mobile Optimized */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            <StatusCard
              title="All"
              fullTitle="All Payments"
              count={Object.values(statusCounts).reduce((a: number, b: unknown) => a + (b as number), 0)}
              icon={<DollarSign className="w-5 h-5 sm:w-6 sm:h-6" />}
              color="bg-blue-600 hover:bg-blue-700 active:bg-blue-800"
              isActive={activeStatus === 'ALL'}
              onClick={() => setActiveStatus('ALL')}
            />
            <StatusCard
              title="Invoice"
              fullTitle="Pending Invoice"
              count={statusCounts.BOOKED || 0}
              icon={<FileText className="w-5 h-5 sm:w-6 sm:h-6" />}
              color="bg-orange-600 hover:bg-orange-700 active:bg-orange-800"
              isActive={activeStatus === 'BOOKED'}
              onClick={() => setActiveStatus('BOOKED')}
            />
            <StatusCard
              title="Pending"
              fullTitle="Payment Pending"
              count={statusCounts.PENDING_PAYMENT || 0}
              icon={<Clock className="w-5 h-5 sm:w-6 sm:h-6" />}
              color="bg-red-600 hover:bg-red-700 active:bg-red-800"
              isActive={activeStatus === 'PENDING_PAYMENT'}
              onClick={() => setActiveStatus('PENDING_PAYMENT')}
            />
            <StatusCard
              title="Completed"
              fullTitle="Payment Completed"
              count={statusCounts.PAYMENT_COMPLETED || 0}
              icon={<CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />}
              color="bg-green-600 hover:bg-green-700 active:bg-green-800"
              isActive={activeStatus === 'PAYMENT_COMPLETED'}
              onClick={() => setActiveStatus('PAYMENT_COMPLETED')}
            />
          </div>

          {/* Search and Filters - Mobile Optimized */}
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
                  <input
                    type="text"
                    placeholder="Search itinerary, guest, vendor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  />
                </div>
              </div>
              
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl border transition-colors touch-manipulation ${
                  showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">Filters</span>
                <span className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`}>▼</span>
              </button>
            </div>

            {showFilters && (
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 animate-in slide-in-from-top-2 duration-200">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Itinerary ID</label>
                      <input
                        type="text"
                        placeholder="Search WH_123"
                        value={searchFilters.itinerarySearch}
                        onChange={(e) => setSearchFilters({...searchFilters, itinerarySearch: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Guest Name</label>
                      <input
                        type="text"
                        placeholder="Search guest name"
                        value={searchFilters.guestName || ''}
                        onChange={(e) => setSearchFilters({...searchFilters, guestName: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Vendor</label>
                      <input
                        type="text"
                        placeholder="Filter by vendor"
                        value={searchFilters.vendor}
                        onChange={(e) => setSearchFilters({...searchFilters, vendor: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Due Date From</label>
                      <input
                        type="date"
                        value={searchFilters.dueDateFrom}
                        onChange={(e) => setSearchFilters({...searchFilters, dueDateFrom: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Due Date To</label>
                      <input
                        type="date"
                        value={searchFilters.dueDateTo}
                        onChange={(e) => setSearchFilters({...searchFilters, dueDateTo: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-center">
                    <button
                      onClick={handleDatabaseSearch}
                      disabled={searching}
                      className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:bg-blue-400 transition-colors touch-manipulation text-sm sm:text-base"
                    >
                      {searching ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Search
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSearchFilters({
                          vendor: '',
                          dueDateFrom: '',
                          dueDateTo: '',
                          itinerarySearch: '',
                          guestName: ''
                        });
                        setSearchQuery('');
                        setExpandedVendors(new Set()); // Clear expanded state
                        setVendorItems({}); // Clear cached vendor items
                        loadDashboardData();
                      }}
                      className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation text-sm sm:text-base"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Vendor Payment Cards */}
          <div className="space-y-6">
            {vendorGroups.map((vendor: any) => {
              const vendorKey = `${vendor.vendor_id}_${vendor.itinerary_id}`;
              const isExpanded = expandedVendors.has(vendorKey);
              
              return (
                <VendorPaymentCard
                  key={vendorKey}
                  vendor={vendor}
                  vendorKey={vendorKey}
                  isExpanded={isExpanded}
                  onExpand={handleVendorExpand}
                  onManagePayment={handleVendorClick}
                  formatDate={formatDate}
                />
              );
            })}
            
            {vendorGroups.length === 0 && !loading && (
              <div className="bg-white rounded-xl sm:rounded-2xl p-8 sm:p-12 shadow-sm text-center text-gray-500">
                <DollarSign className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No payment items found</h3>
                <p className="text-sm sm:text-base">No payment items match the selected status and filters.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Management Modal - Mobile Optimized */}
      {showVendorModal && selectedVendorGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl sm:w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-4">
                  Add Payment - {selectedVendorGroup.vendor_name}
                </h3>
                <button
                  onClick={() => setShowVendorModal(false)}
                  className="text-gray-400 hover:text-gray-600 active:text-gray-800 text-2xl p-1 touch-manipulation flex-shrink-0"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-xl">
                <div className="font-medium text-gray-900 text-sm sm:text-base">WH_{selectedVendorGroup.itinerary_id}</div>
                <div className="text-sm text-gray-600 truncate">{selectedVendorGroup.guest_name}</div>
                <div className="flex justify-between items-center mt-2">
                  <div className="text-lg sm:text-xl font-bold text-gray-900">
                    Total: {formatCurrency(selectedVendorGroup.total_amount)}
                  </div>
                  <div className="text-sm">
                    {(() => {
                      const totalPaid = paymentHistory.reduce((sum: number, p: any) => sum + (p.payment_amount || 0), 0);
                      const outstanding = selectedVendorGroup.total_amount - totalPaid;
                      return (
                        <>
                          <span className="text-gray-600">Paid: {formatCurrency(totalPaid)} | </span>
                          <span className="text-red-600 font-medium">Outstanding: {formatCurrency(outstanding)}</span>
                        </>
                      );
                    })()
                    }
                  </div>
                </div>
              </div>

              {/* Payment History Section */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Payment History</h4>
                {loadingHistory ? (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    Loading payment history...
                  </div>
                ) : paymentHistory.length > 0 ? (
                  <div className="space-y-2">
                    {paymentHistory.map((payment: any, index: number) => (
                      <div key={payment.id || index} className="bg-gray-50 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-900">{formatCurrency(payment.payment_amount)}</div>
                            <div className="text-gray-600">{new Date(payment.payment_date).toLocaleDateString('en-GB')}</div>
                            {payment.payment_reference && (
                              <div className="text-gray-500">Ref: {payment.payment_reference}</div>
                            )}
                            {payment.receipt_file && (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-green-600">📎 Receipt:</span>
                                <button
                                  onClick={() => window.open(payment.receipt_file.view_url, '_blank')}
                                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                  {payment.receipt_file.filename}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(payment.created_at).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                        {payment.notes && (
                          <div className="text-xs text-gray-600 mt-1">{payment.notes}</div>
                        )}
                      </div>
                    ))}
                    <div className="bg-blue-50 rounded-lg p-3 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-blue-900">Total Paid:</span>
                        <span className="font-bold text-blue-900">
                          {formatCurrency(paymentHistory.reduce((sum: number, p: any) => sum + (p.payment_amount || 0), 0))}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                    No payments recorded yet
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h5 className="font-medium text-blue-900 mb-2">Add New Payment</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <input
                      type="number"
                      value={paymentForm.actual_amount}
                      onChange={(e) => setPaymentForm({...paymentForm, actual_amount: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter amount"
                      min="0"
                      step="1000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                  <input
                    type="text"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Bank transfer ref, check number, etc."
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Receipt</label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setPaymentForm({...paymentForm, receipt_file: e.target.files?.[0] || null})}
                    className="block w-full text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    required
                  />
                  {paymentForm.receipt_file && (
                    <div className="mt-1 text-xs text-green-600">
                      Selected: {paymentForm.receipt_file.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end sm:space-x-4">
                <button
                  onClick={() => setShowVendorModal(false)}
                  className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation text-sm sm:text-base"
                >
                  Add Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal - Mobile Optimized */}
      {showFileViewer && currentFile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] sm:w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-base sm:text-lg font-semibold truncate pr-4">{currentFile.filename}</h3>
              <button
                onClick={() => setShowFileViewer(false)}
                className="text-gray-400 hover:text-gray-600 active:text-gray-800 text-2xl p-1 touch-manipulation flex-shrink-0"
              >
                ×
              </button>
            </div>
            <div className="p-2 sm:p-4 max-h-[85vh] sm:max-h-[80vh] overflow-auto">
              {currentFile.filename?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={currentFile.url}
                  className="w-full h-[75vh] sm:h-[70vh]"
                  title="PDF Preview"
                />
              ) : (
                <img
                  src={currentFile.url}
                  alt={currentFile.filename}
                  className="max-w-full max-h-full object-contain mx-auto"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsPaymentDashboard;